import shaka from 'shaka-player';
import { isSafari } from 'utils/fnDevices';
import * as Sentry from '@sentry/react';
import { YouboraOptions } from 'types/Tracking';
import { compareBitrateTracks, compareTracks, getThumbnailSize } from 'utils/fnData';
import { getLocalStorage, setLocalStorage } from 'utils/fnStorage';
import { isCatchupPlayerAsset, isLivePlayerAsset, isVodPlayerAsset } from 'utils/fnTypeGuards';
import ApplicationConfig from 'providers/useConfig/ApplicationConfig';
import { IPlayer } from './abstract/IPlayer';
import {
    BITRATE_TRACK_AUTO,
    BitrateTrack,
    PlayerAsset,
    PlayerConfig,
    PlayingAsset,
    SUBTITLE_NONE,
    SUBTITLE_OFF,
    Track,
} from '../../types/Player';
import YouboraTracking from './YouboraTracking';
import DRM from './DRM';
import { getSessionAudioLanguage, getSessionSubtitlesLanguage } from './sessionLanguages';
import {
    HEARING_IMPAIRED_ROLES,
    FROZEN_AT_END_MS,
    FROZEN_MID_STREAM_MS,
    MANIFEST_STATIC_MS,
    MIN_MOVEMENT_SECONDS,
    NETWORK_ERROR_CODE,
    PLAYER_ERROR_INTERRUPT,
    VISUALLY_IMPAIRED_ROLES,
    PROGRESS_CHECK_INTERVAL_MS,
    SEEK_END_TOLERANCE,
    STREAM_ERRORS,
    STREAMING_FAILURE_MAX_RETRIES,
    NETWORK_ERROR_CATEGORY,
    DOWNLOADS_STALLED_MS,
    RECOVERY_KICK_GRACE_MS,
    STREAMING_RETRY_GRACE_MS,
    STARVED_RETRY_GRACE_MS,
    STARVED_SILENCE_MS,
    STARVED_KICK_GRACE_MS,
    STARVED_BUFFER_SECONDS,
    STREAM_RELOAD_MAX_ATTEMPTS,
    STREAM_RELOAD_MIN_GAP_MS,
    STREAM_RELOAD_COOLDOWN_MS,
    RECOVERABLE_MEDIA_ERROR_CODES,
    STUCK_BUFFERING_MS,
    STUCK_BUFFERING_MAX_MS,
    STUCK_SEEK_MS,
    FORCED_RELOAD_AFTER_MS,
    GAP_PADDING_SECONDS,
    STARTUP_REBUFFERING_GOAL_SECONDS,
    LIVE_EDGE_START_OFFSET_SECONDS,
    BANDWIDTH_RATIO_FAST,
    BANDWIDTH_RATIO_MARGINAL,
    BANDWIDTH_RATIO_SMOOTHING,
    BANDWIDTH_SAMPLE_INTERVAL_MS,
    MANIFEST_UPDATE_PERIOD_SECONDS,
    STREAMING_RETRY_PARAMETERS,
} from './playerConstants';

class ShakaPlayer extends IPlayer {
    private storedVolume = 0;

    private engine = null;

    private asset: PlayerAsset = null;

    private availableSubtitles: Track[] = [];

    private availableAudios: Track[] = [];

    private availableBitrateTracks: any = [];

    private eventManager;

    private shouldResetBeforeSet = false;

    private stoppedAtTime: number = null;

    private eventManagerEvents = ['buffering', 'trackschanged'];

    private currentAudio = null;

    private currentSubtitle = null;

    private currentBitrate = null;

    private youbora: YouboraTracking = null;

    private progressCheckInterval: number | null = null;

    private lastProgressTime = -1;

    private lastProgressAt = 0;

    private lastSeekRangeEnd = -1;

    private manifestStaticSince = 0;

    private nudgeAttempted = false;

    private endOfStreamEmitted = false;

    private detachChronoListeners: () => void = () => {};

    private streamingFailureRetries = 0;

    private streamReloads = 0;

    private lastStreamReloadAt = 0;

    private reloadInProgress = false;

    private reloadGaveUp = false;

    private lastBufferEnd = -1;

    private bufferStaticSince = 0;

    private downloadKickAt = 0;

    private segmentRequestsInFlight = 0;

    private lastSegmentActivityAt = 0;

    private lastSegmentFailureAt = 0;

    private stuckBufferingSince = 0;

    private stuckSeekSince = 0;

    private reloadRetryAt = 0;

    private reloadResumeAt: number | null = null;

    private reloadResumePlaying = false;

    private steadyRebufferingGoal: number | null = null;

    private startupBufferingActive = false;

    private startupRebufferingGoal: number | null = null;

    private activeStartupGoal: number | null = null;

    private lastBandwidthSampleAt = 0;

    private stallChecksSuspended = false;

    constructor() {
        super();
        this.TAG = 'Shaka Player';

        this.eventManager = new shaka.util.EventManager();

        this.drmHandler = DRM;

        this.youbora = YouboraTracking.getInstance();

        this.init();
    }

    static getPlayerVersion = (): string => shaka.Player.version;

    private extractDrmErrorMessage = (data: any[]): string | null => {
        if (!Array.isArray(data)) return null;
        return data.reduce<string | null>((found, item) => {
            if (found) return found;
            if (typeof item === 'string') {
                if (STREAM_ERRORS.includes(item)) return item;
                try {
                    const parsed = JSON.parse(item);
                    if (parsed?.error_code) return parsed.error_code;
                } catch {
                    /* invalid JSON, skip */
                }
            } else if (typeof item === 'object' && item !== null) {
                if (item?.error_code) return item.error_code;
                if (Array.isArray(item?.data)) return this.extractDrmErrorMessage(item.data);
            }
            return null;
        }, null);
    };

    private restartStallTimers = (now: number) => {
        this.lastProgressAt = now;
        this.manifestStaticSince = now;
        this.nudgeAttempted = false;
        this.bufferStaticSince = now;
        this.downloadKickAt = 0;
        this.lastSegmentActivityAt = now;
        this.stuckBufferingSince = 0;
        this.stuckSeekSince = 0;
    };

    private resetProgressTracking = () => {
        const now = Date.now();

        this.lastProgressTime = -1;
        this.lastSeekRangeEnd = -1;
        this.endOfStreamEmitted = false;
        this.lastBufferEnd = -1;
        this.lastBandwidthSampleAt = now;
        this.restartStallTimers(now);
    };

    private isSilentInBackground = (): boolean => document.visibilityState === 'hidden' && (this.video.muted || this.video.volume === 0);

    private getBufferedEnd = (): number => {
        const buffered = this.video?.buffered;
        const time = this.video?.currentTime ?? 0;

        if (!buffered) {
            return time;
        }

        for (let index = 0; index < buffered.length; index += 1) {
            if (time >= buffered.start(index) - 0.5 && time <= buffered.end(index)) {
                return buffered.end(index);
            }
        }

        return time;
    };

    private trackSegmentActivity = () => {
        const segmentType = shaka.net.NetworkingEngine.RequestType.SEGMENT;

        this.engine.getNetworkingEngine()?.registerRequestFilter((type: number, request: any) => {
            if (type !== segmentType || request?.attempt) {
                return;
            }

            this.segmentRequestsInFlight += 1;
            this.lastSegmentActivityAt = Date.now();
        });

        this.engine.addEventListener('downloadcompleted', (event: any) => {
            if (event?.requestType !== segmentType) {
                return;
            }

            this.segmentRequestsInFlight = Math.max(0, this.segmentRequestsInFlight - 1);
            this.lastSegmentActivityAt = Date.now();
        });

        this.engine.addEventListener('downloadfailed', (event: any) => {
            if (event?.requestType !== segmentType) {
                return;
            }

            this.segmentRequestsInFlight = Math.max(0, this.segmentRequestsInFlight - 1);
            this.lastSegmentFailureAt = Date.now();
        });
    };

    private getStartupRebufferingGoal = (): number => this.startupRebufferingGoal ?? STARTUP_REBUFFERING_GOAL_SECONDS;

    private readBandwidthRatio = (): number | null => {
        const stored = Number(getLocalStorage('playerBandwidthRatio'));

        return Number.isFinite(stored) && stored > 0 ? stored : null;
    };

    private recordBandwidthSample = (now: number) => {
        if (now - this.lastBandwidthSampleAt < BANDWIDTH_SAMPLE_INTERVAL_MS) {
            return;
        }

        this.lastBandwidthSampleAt = now;

        const stats = this.engine?.getStats();
        const estimated = stats?.estimatedBandwidth;
        const stream = stats?.streamBandwidth;

        if (!(estimated > 0) || !(stream > 0)) {
            return;
        }

        const sample = estimated / stream;
        const previous = this.readBandwidthRatio();
        const smoothed = previous === null ? sample : previous + (sample - previous) * BANDWIDTH_RATIO_SMOOTHING;

        setLocalStorage('playerBandwidthRatio', Math.round(smoothed * 100) / 100);
    };

    private resolveAdaptiveStartupGoal = (configuredGoal: number, steadyGoal: number): number => {
        const ratio = this.readBandwidthRatio();

        if (ratio === null || ratio >= BANDWIDTH_RATIO_FAST) {
            return configuredGoal;
        }

        if (ratio <= BANDWIDTH_RATIO_MARGINAL) {
            return steadyGoal;
        }

        const position = (ratio - BANDWIDTH_RATIO_MARGINAL) / (BANDWIDTH_RATIO_FAST - BANDWIDTH_RATIO_MARGINAL);

        return Math.round(steadyGoal + (configuredGoal - steadyGoal) * position);
    };

    private beginStartupBuffering = () => {
        if (!this.engine) {
            return;
        }

        const configuredGoal = this.getStartupRebufferingGoal();
        const configured = this.engine.getConfiguration()?.streaming?.rebufferingGoal;

        if (typeof configured === 'number' && configured > configuredGoal) {
            this.steadyRebufferingGoal = configured;
        }

        if (this.steadyRebufferingGoal === null) {
            return;
        }

        const startupGoal = this.resolveAdaptiveStartupGoal(configuredGoal, this.steadyRebufferingGoal);

        if (startupGoal >= this.steadyRebufferingGoal) {
            this.log('info', 'Startup buffering split skipped', startupGoal, this.readBandwidthRatio());
            return;
        }

        this.activeStartupGoal = startupGoal;
        this.startupBufferingActive = true;
        this.engine.configure({ streaming: { rebufferingGoal: startupGoal } });
    };

    private endStartupBuffering = () => {
        if (!this.startupBufferingActive) {
            return;
        }

        this.startupBufferingActive = false;
        this.activeStartupGoal = null;

        if (this.engine && this.steadyRebufferingGoal !== null) {
            this.engine.configure({ streaming: { rebufferingGoal: this.steadyRebufferingGoal } });
        }
    };

    private getBufferProfile = () => {
        const buffers = ApplicationConfig.app_config?.player_settings?.buffers;
        const asset = this.asset as PlayingAsset;

        if (isLivePlayerAsset(asset)) return buffers?.live;
        if (isCatchupPlayerAsset(asset)) return buffers?.tv;
        if (isVodPlayerAsset(asset)) return buffers?.vod;

        return null;
    };

    private applyBufferProfile = () => {
        const profile = this.getBufferProfile();

        if (!profile) {
            return;
        }

        this.configureEngine({
            streaming: {
                bufferingGoal: profile.bufferingGoal,
                rebufferingGoal: profile.rebufferingGoal,
                bufferBehind: profile.bufferBehind,
                retryParameters: STREAMING_RETRY_PARAMETERS,
            },
            manifest: { updatePeriod: MANIFEST_UPDATE_PERIOD_SECONDS },
            startupRebufferingGoal: profile.startupRebufferingGoal,
        });
    };

    private usesNativeHls = (): boolean => {
        const config = this.engine?.getConfiguration();
        const servers = config?.drm?.servers ?? {};

        return (
            isSafari() && config?.streaming?.useNativeHlsForFairPlay && Boolean(servers['com.apple.fps'] || servers['com.apple.fps.1_0'])
        );
    };

    private resolveLoadStartTime = (startTime: number): number | null => {
        if (startTime || !isLivePlayerAsset(this.asset as PlayingAsset)) {
            return startTime;
        }

        if (this.usesNativeHls()) {
            return null;
        }

        return -(this.getBufferProfile()?.liveEdgeOffset ?? LIVE_EDGE_START_OFFSET_SECONDS);
    };

    private isNetworkQuiet = (now: number, starving: boolean): boolean => {
        const graceMs = starving ? STARVED_RETRY_GRACE_MS : STREAMING_RETRY_GRACE_MS;

        return window.navigator.onLine && now - this.lastSegmentFailureAt >= graceMs;
    };

    private isReloadOverdue = (now: number): boolean =>
        window.navigator.onLine && this.downloadKickAt > 0 && now - this.downloadKickAt >= FORCED_RELOAD_AFTER_MS;

    private getLoadMode = (): number | null => {
        try {
            return this.engine?.getLoadMode?.() ?? null;
        } catch {
            return null;
        }
    };

    private isMediaSourceMode = (): boolean => this.getLoadMode() === shaka.Player.LoadMode.MEDIA_SOURCE;

    private isEngineLoaded = (): boolean => {
        const mode = this.getLoadMode();

        return mode === shaka.Player.LoadMode.MEDIA_SOURCE || mode === shaka.Player.LoadMode.SRC_EQUALS;
    };

    private isEngineBuffering = (): boolean => {
        try {
            return this.engine?.isBuffering?.() === true;
        } catch {
            return false;
        }
    };

    private checkSilentStreaming = (now: number, seekRangeEnd: number | null) => {
        if (this.reloadInProgress || !this.engine) {
            return;
        }

        const bufferEnd = this.getBufferedEnd();
        const moreToFetch = seekRangeEnd == null || bufferEnd < seekRangeEnd - SEEK_END_TOLERANCE;

        if (!moreToFetch || bufferEnd > this.lastBufferEnd + MIN_MOVEMENT_SECONDS) {
            this.lastBufferEnd = bufferEnd;
            this.bufferStaticSince = now;
            this.downloadKickAt = 0;
            return;
        }

        const starving = this.isEngineBuffering() || bufferEnd - this.video.currentTime < STARVED_BUFFER_SECONDS;
        const silentForMs = Math.min(now - this.bufferStaticSince, now - this.lastSegmentActivityAt);

        if (silentForMs < (starving ? STARVED_SILENCE_MS : DOWNLOADS_STALLED_MS)) {
            return;
        }

        if (!starving && this.segmentRequestsInFlight > 0) {
            return;
        }

        if (!this.downloadKickAt) {
            this.downloadKickAt = now;

            if (this.resumeStreaming() || !this.isMediaSourceMode()) {
                return;
            }

            this.log('warn', 'Streaming engine refused to retry, reloading');
            this.reloadStream();
            return;
        }

        if (now - this.downloadKickAt < (starving ? STARVED_KICK_GRACE_MS : RECOVERY_KICK_GRACE_MS)) {
            return;
        }

        if (!this.isNetworkQuiet(now, starving) && !this.isReloadOverdue(now)) {
            return;
        }

        this.reloadStream();
    };

    private checkStuckBuffering = (now: number) => {
        if (this.reloadInProgress || !window.navigator.onLine || !this.isEngineLoaded() || !this.isEngineBuffering()) {
            this.stuckBufferingSince = 0;
            return;
        }

        if (!this.stuckBufferingSince) {
            this.stuckBufferingSince = now;
            return;
        }

        const bufferStalled = now - this.bufferStaticSince >= STUCK_BUFFERING_MS;

        if (now - this.stuckBufferingSince < (bufferStalled ? STUCK_BUFFERING_MS : STUCK_BUFFERING_MAX_MS)) {
            return;
        }

        this.stuckBufferingSince = now;
        this.log('warn', 'Buffering never cleared, reloading');
        this.reloadStream();
    };

    private reportUnrecoverable = (error: any) => {
        if (this.reloadGaveUp) {
            return;
        }

        this.reloadGaveUp = true;

        if (this.handler?.errorHandler && typeof this.handler.errorHandler === 'function') {
            this.handler.errorHandler({
                code: error?.code ?? (window.navigator.onLine ? undefined : NETWORK_ERROR_CODE),
                category: error?.category ?? NETWORK_ERROR_CATEGORY,
                message: this.extractDrmErrorMessage(error?.data) || error?.message || null,
            });
        }
    };

    private reloadStream = async (fallbackError: any = null) => {
        const manifestUrl = this.asset?.stream_url;

        if (this.reloadInProgress || !this.engine || !manifestUrl) {
            return;
        }

        if (!window.navigator.onLine) {
            this.reloadRetryAt = this.reloadRetryAt || Date.now();
            return;
        }

        const now = Date.now();

        if (now - this.lastStreamReloadAt < STREAM_RELOAD_MIN_GAP_MS) {
            return;
        }

        if (now - this.lastStreamReloadAt > STREAM_RELOAD_COOLDOWN_MS) {
            this.streamReloads = 0;
        }

        if (this.streamReloads >= STREAM_RELOAD_MAX_ATTEMPTS) {
            this.reportUnrecoverable(fallbackError);
            return;
        }

        this.reloadInProgress = true;
        this.streamReloads += 1;
        this.lastStreamReloadAt = now;
        this.segmentRequestsInFlight = 0;

        if (this.reloadResumeAt === null) {
            this.reloadResumeAt = this.video?.currentTime ?? 0;
            this.reloadResumePlaying = !this.video?.paused;
        }

        const resumeAt = this.reloadResumeAt;
        const wasPlaying = this.reloadResumePlaying;

        this.log('warn', 'Streaming wedged, reloading', manifestUrl, resumeAt);

        this.youbora.stopView();
        this.endStartupBuffering();

        try {
            await this.engine.load(manifestUrl, this.resolveLoadStartTime(resumeAt));

            this.reloadResumeAt = null;
            this.reloadResumePlaying = false;

            this.getAudioTracks();
            this.getSubtitleTracks();
            this.getVariantTracks();

            if (this.currentAudio) {
                this.setAudioLanguage(this.currentAudio);
            }

            this.setTextLanguage(this.currentSubtitle);

            if (this.currentBitrate) {
                this.setBitrateTrack(this.currentBitrate);
            }

            if (wasPlaying) {
                this.play();
            }

            this.reloadRetryAt = 0;

            if (this.reloadGaveUp) {
                this.reloadGaveUp = false;
                this.handler?.onStreamRecovered?.();
            }
        } catch (e) {
            this.log('warn', 'Stream reload failed', e);

            const httpStatus = Array.isArray(e?.data) ? e.data[1] : null;
            const streamGone = typeof httpStatus === 'number' && httpStatus >= 400 && httpStatus < 500;

            if (e?.code !== PLAYER_ERROR_INTERRUPT && (streamGone || this.streamReloads >= STREAM_RELOAD_MAX_ATTEMPTS)) {
                this.reportUnrecoverable(e);
            } else if (e?.code !== PLAYER_ERROR_INTERRUPT) {
                this.reloadRetryAt = Date.now() + STREAM_RELOAD_MIN_GAP_MS;
            }
        } finally {
            this.reloadInProgress = false;
            this.resetProgressTracking();
        }
    };

    private getSeekRangeEnd = (): number | null => {
        try {
            const end = this.engine?.seekRange?.()?.end;
            return Number.isFinite(end) ? end : null;
        } catch {
            return null;
        }
    };

    private emitEndOfStream = () => {
        if (this.endOfStreamEmitted) {
            return;
        }

        this.endOfStreamEmitted = true;
        this.video.dispatchEvent(new Event('ended'));
    };

    /**
     * Safari's native HLS never fires 'ended' when playback runs past the manifest's seek range: it
     * freezes with paused=false/ended=false and buffers forever, so nothing ever clears the spinner.
     */
    private monitorPlaybackProgress = () => {
        if (!this.video || this.progressCheckInterval) {
            return;
        }

        this.progressCheckInterval = window.setInterval(() => {
            if (!this.video) {
                return;
            }

            const now = Date.now();

            if (this.reloadRetryAt && now >= this.reloadRetryAt && window.navigator.onLine) {
                this.reloadRetryAt = 0;
                this.log('warn', 'Previous reload failed, retrying');
                this.reloadStream();
                return;
            }

            if (this.video.paused || this.video.ended) {
                return;
            }

            if (this.reloadInProgress || this.reloadRetryAt || !this.isEngineLoaded()) {
                return;
            }

            if (this.isSilentInBackground()) {
                this.stallChecksSuspended = true;
                return;
            }

            if (this.stallChecksSuspended) {
                this.stallChecksSuspended = false;
                this.restartStallTimers(now);
            }

            this.recordBandwidthSample(now);

            if (this.video.seeking) {
                if (!this.stuckSeekSince || !window.navigator.onLine) {
                    this.stuckSeekSince = now;
                } else if (now - this.stuckSeekSince >= STUCK_SEEK_MS && this.isEngineLoaded()) {
                    this.stuckSeekSince = now;
                    this.log('warn', 'Seek never completed, reloading');
                    this.reloadStream();
                }

                return;
            }

            this.stuckSeekSince = 0;

            const seekRangeEnd = this.getSeekRangeEnd();

            if (seekRangeEnd != null && Math.abs(seekRangeEnd - this.lastSeekRangeEnd) >= MIN_MOVEMENT_SECONDS) {
                this.lastSeekRangeEnd = seekRangeEnd;
                this.manifestStaticSince = now;
            }

            this.checkSilentStreaming(now, seekRangeEnd);
            this.checkStuckBuffering(now);

            if (Math.abs(this.video.currentTime - this.lastProgressTime) >= MIN_MOVEMENT_SECONDS) {
                this.lastProgressTime = this.video.currentTime;
                this.lastProgressAt = now;
                this.nudgeAttempted = false;
                return;
            }

            const frozenForMs = now - this.lastProgressAt;
            const atEnd = seekRangeEnd != null && this.video.currentTime >= seekRangeEnd - SEEK_END_TOLERANCE;
            const manifestFinished = now - this.manifestStaticSince >= MANIFEST_STATIC_MS;
            if (frozenForMs >= FROZEN_AT_END_MS && atEnd && manifestFinished && window.navigator.onLine) {
                this.emitEndOfStream();
            } else if (frozenForMs >= FROZEN_MID_STREAM_MS && !this.nudgeAttempted) {
                this.nudgeAttempted = true;
                this.seekTo(this.video.currentTime + 0.1);
                this.play();
            }
        }, PROGRESS_CHECK_INTERVAL_MS);
    };

    private isDisplayableSubtitle = (track: any): boolean => {
        const codecs = track?.codecs ?? null;
        const mimeType = track?.mimeType ?? null;

        if (codecs == null) {
            return mimeType == null || mimeType.includes('vtt');
        }

        return codecs.includes('vtt');
    };

    private getSubtitleTracks = () => {
        const tracks = this.engine.getTextTracks();

        this.availableSubtitles = [{ role: null, label: null, raw: null, language: SUBTITLE_NONE }];

        this.availableSubtitles = [
            ...this.availableSubtitles,
            ...tracks
                .filter((track) => track.language != null && track.language !== '')
                .filter((track) => this.isDisplayableSubtitle(track)) // Safari reports no codec and Chrome can only display WebVTT
                .map((track) => ({
                    role: track?.kind ?? track?.roles?.[0],
                    label: track.label,
                    language: track.language,
                    raw: track,
                })),
        ];

        return this.availableSubtitles;
    };

    private getVariantTracks = () => {
        let tracks = this.engine.getVariantTracks();

        const selectedTrack = tracks.find((track) => track.active);

        if (selectedTrack) {
            // Filter by current audio language and channel count.
            tracks = tracks.filter(
                (track) => track.language === selectedTrack.language && track.channelsCount === selectedTrack.channelsCount
            );
        }

        // Remove duplicate entries with the same resolution
        tracks = tracks.filter((track, idx) => {
            // Keep the first one with the same height or bandwidth.
            const otherIdx = tracks.findIndex((t) => t.height === track.height);
            return otherIdx === idx;
        });

        // Sort the tracks by height or bandwidth depending on content type.
        tracks.sort((t1, t2) => t2.height - t1.height);

        this.availableBitrateTracks = tracks.length > 1 ? [{ height: null, width: null, bandwidth: BITRATE_TRACK_AUTO }] : [];

        this.availableBitrateTracks = [
            ...this.availableBitrateTracks,
            ...tracks
                .filter((track) => track.language != null && track.language !== '')
                .map((track) => ({
                    height: track.height,
                    width: track.width,
                    bandwidth: track.bandwidth,
                    raw: track,
                })),
        ];

        return this.availableBitrateTracks;
    };

    private getImageTrack = () => {
        const imageTracks = this.engine.getImageTracks();

        if (imageTracks) {
            const [availableImages] = imageTracks;
            return availableImages;
        }

        return null;
    };

    private getThumbnailImage = async (id: number, position: number) => {
        const thumbnailImage = await this.engine.getThumbnails(id, position);
        if (thumbnailImage) {
            return getThumbnailSize(thumbnailImage);
        }
        return null;
    };

    private toAudioTrack = (variant: any): Track => ({
        role: variant?.kind ?? variant?.roles?.[0],
        label: variant.label,
        language: variant.language,
        channelsCount: variant.channelsCount,
        raw: variant,
    });

    private getAudioTracks = () => {
        const tracks = this.engine
            .getVariantTracks()
            .filter((track) => track.language != null && track?.language !== '')
            .map(this.toAudioTrack);

        this.availableAudios = tracks.filter((track, idx) => tracks.findIndex((other) => compareTracks(other, track)) === idx);

        return this.availableAudios;
    };

    private getActiveVariant = () => this.engine?.getVariantTracks().find((variant) => variant.active);

    private handleStreamingFailure = (error: any) => {
        if (this.streamingFailureRetries < STREAMING_FAILURE_MAX_RETRIES) {
            this.streamingFailureRetries += 1;
            this.engine.retryStreaming();
            return;
        }

        if (this.handler?.errorHandler && typeof this.handler.errorHandler === 'function') {
            this.handler.errorHandler({
                code: error?.code,
                category: error?.category,
                message: this.extractDrmErrorMessage(error?.data) || error?.message,
            });
        }
    };

    private configurePreferredTracks = () => {
        const audioLanguage = getSessionAudioLanguage() || getLocalStorage('primaryAudioLanguage') || '';
        const subtitleLanguage = getSessionSubtitlesLanguage() || getLocalStorage('primarySubtitlesLanguage') || '';
        const textLanguage = subtitleLanguage === SUBTITLE_NONE || subtitleLanguage === SUBTITLE_OFF ? '' : subtitleLanguage;

        this.engine.configure({
            preferredAudio: [
                {
                    language: audioLanguage,
                    role: getLocalStorage('accessibilitySound') ? VISUALLY_IMPAIRED_ROLES[0] : '',
                    label: '',
                    channelCount: 2,
                    codec: '',
                },
            ],
            preferredText: textLanguage
                ? [
                      {
                          language: textLanguage,
                          role: getLocalStorage('accessibilitySubs') ? HEARING_IMPAIRED_ROLES[0] : '',
                          forced: false,
                      },
                  ]
                : [],
        });
    };

    setVideo = (video?: HTMLVideoElement): ShakaPlayer => {
        this.video = video;
        return this;
    };

    init = () => {
        this.log('info', 'init');

        const { Player } = shaka;

        if (!this.video) {
            return;
        }

        if (!Player.isBrowserSupported()) {
            this.log('error', 'Not supported browser');
        }
    };

    static getChromeCastCustomData() {
        return {};
    }

    addEventListener = (event: string, callback: (object?: {}) => void) => {
        if (this.eventManagerEvents.includes(event)) {
            this.addEventManagerListener(event, callback);
        } else {
            this.video.addEventListener(event, callback);
        }
    };

    removeEventListener = (event: string, callback: (object?: {}) => void) => {
        if (this.eventManagerEvents.includes(event)) {
            this.removeEventManagerListener(event, callback);
        } else {
            this.video.removeEventListener(event, callback);
        }
    };

    addEventManagerListener = (event, cb) => {
        this.eventManager.listen(this.engine, event, cb);
    };

    removeEventManagerListener = (event, cb) => {
        this.eventManager.unlisten(this.engine, event, cb);
    };

    stopPlayback = (): void => {
        if (!this.engine) {
            return;
        }

        if (this.video && this.stoppedAtTime === null) {
            this.stoppedAtTime = this.reloadResumeAt ?? this.video.currentTime;
        }

        this.detachChronoListeners();
        this.youbora.stopView();
        this.engine.unload().catch(() => {});
    };

    resetPlayer = () => {
        if (this.progressCheckInterval) {
            clearInterval(this.progressCheckInterval);
            this.progressCheckInterval = null;
        }

        this.resetProgressTracking();

        this.asset = null;
        this.shouldResetBeforeSet = false;
        this.stoppedAtTime = null;
        this.streamingFailureRetries = 0;
        this.reloadGaveUp = false;
        this.reloadRetryAt = 0;
        this.reloadResumeAt = null;
        this.reloadResumePlaying = false;
        this.startupBufferingActive = false;
        this.activeStartupGoal = null;
        this.steadyRebufferingGoal = null;

        const youboraTracking = YouboraTracking.getInstance();

        youboraTracking.setPlaybackStarted(false);
        youboraTracking.stopView();

        if (this.engine) {
            this.detachChronoListeners();
            this.engine.resetConfiguration();
            this.engine.unload();
        }
    };

    setAsset = async (assetDetails: PlayerAsset, startTime: number, suffixDomain: 'tv' | 'vod', preparedCb: () => void) => {
        /*  if (this.asset && this.asset.stream_url === assetDetails.stream_url) {
            // already playing the same asset
            return;
        } */

        this.asset = assetDetails;
        this.stoppedAtTime = null;
        this.streamReloads = 0;
        this.lastStreamReloadAt = 0;
        this.reloadGaveUp = false;
        this.segmentRequestsInFlight = 0;
        this.lastSegmentFailureAt = 0;
        this.reloadRetryAt = 0;
        this.reloadResumeAt = null;
        this.reloadResumePlaying = false;

        this.resetProgressTracking();

        if (this.engine && this.asset) {
            const manifestUrl = this.asset.stream_url;

            if (manifestUrl) {
                const chronos: Record<string, number> = {};

                this.youbora.stopView();

                try {
                    if (this.drmHandler) {
                        const drmConfigStartedAt = performance.now();

                        await this.drmHandler.configurePlayer(this.engine, this.asset);

                        if (isSafari()) {
                            chronos.drmCertificate = Math.round(performance.now() - drmConfigStartedAt);
                        }
                    }

                    const asset = this.asset as any;
                    const formatContentType = (type: string, hasChannelId: boolean) => {
                        if (hasChannelId) return 'Broadcast';
                        const normalized = String(type).toLowerCase();
                        return normalized === 'vod' ? 'VOD' : normalized.charAt(0).toUpperCase() + normalized.slice(1);
                    };

                    const youboraOptions: YouboraOptions = {
                        'content.id': asset?.id || '',
                        'content.title': asset?.title || '',
                        'content.episodeTitle': String(asset?.episode_title || ''),
                        'content.type': formatContentType(asset?.type || asset?.use_case, !!asset?.channel_id),
                        'content.playbackType': YouboraTracking.getPlaybackType(asset),
                    };

                    if (asset?.channel_id) {
                        youboraOptions['content.channel'] = asset.channel_id;
                    }

                    if (this.asset.analytics_npaw) {
                        this.youbora.setServerAnalytics(this.asset.analytics_npaw);
                    }

                    this.youbora.setShakaAdapter(this.engine);

                    this.youbora.setOptions(youboraOptions);

                    this.configurePreferredTracks();
                    this.applyBufferProfile();

                    this.streamingFailureRetries = 0;
                    this.engine.configure({
                        streaming: { failureCallback: this.handleStreamingFailure, gapPadding: GAP_PADDING_SECONDS },
                    });

                    let manifestParsedAt = 0;
                    let drmSessionAt = 0;
                    let streamingStartedAt = 0;
                    const loadStartedAt = performance.now();

                    const onManifestParsed = () => {
                        manifestParsedAt = performance.now();
                        chronos.manifest = Math.round(manifestParsedAt - loadStartedAt);
                    };

                    const onDrmSessionUpdate = () => {
                        if (drmSessionAt) {
                            return;
                        }

                        drmSessionAt = performance.now();
                        this.engine.removeEventListener('drmsessionupdate', onDrmSessionUpdate);
                        this.youbora.setChronos({ drmLicence: Math.round(drmSessionAt - (manifestParsedAt || loadStartedAt)) });
                    };

                    this.detachChronoListeners();

                    this.detachChronoListeners = () => {
                        this.engine.removeEventListener('manifestparsed', onManifestParsed);
                        this.engine.removeEventListener('drmsessionupdate', onDrmSessionUpdate);
                        this.detachChronoListeners = () => {};
                    };

                    this.engine.addEventListener('manifestparsed', onManifestParsed);
                    this.engine.addEventListener('drmsessionupdate', onDrmSessionUpdate);

                    this.beginStartupBuffering();

                    try {
                        await this.engine.load(manifestUrl, this.resolveLoadStartTime(startTime));
                    } finally {
                        this.engine.removeEventListener('manifestparsed', onManifestParsed);
                    }

                    streamingStartedAt = performance.now();
                    chronos.initialLoad = Math.round(streamingStartedAt - (drmSessionAt || manifestParsedAt || loadStartedAt));

                    if (!drmSessionAt) {
                        const stats = this.engine.getStats();
                        const licenceSeconds = [stats?.licenseTime, stats?.drmTimeSeconds].find(
                            (value) => Number.isFinite(value) && value > 0
                        );

                        if (licenceSeconds) {
                            chronos.drmLicence = Math.round(licenceSeconds * 1000);
                        }
                    }

                    this.youbora.setChronos(chronos);

                    this.log('log', 'Manifest is ok', manifestUrl);
                    preparedCb();

                    if (isSafari() && startTime > 0) {
                        // this is needed for Safari because load() startTime does not work properly
                        const bufferingCallback = (event) => {
                            if (event.buffering === false) {
                                this.video.currentTime = startTime;

                                this.removeEventListener('buffering', bufferingCallback);
                            }
                        };

                        this.addEventListener('buffering', bufferingCallback);
                    }

                    this.getAudioTracks();
                    this.getSubtitleTracks();
                } catch (e) {
                    this.log('warn', 'Manifest error', manifestUrl, e);

                    if (e?.code !== PLAYER_ERROR_INTERRUPT) {
                        this.endStartupBuffering();
                    }

                    const youboraTracking = YouboraTracking.getInstance();
                    youboraTracking.setPlaybackStarted(false);

                    if (e.code && e.code !== PLAYER_ERROR_INTERRUPT) {
                        youboraTracking.stopView();

                        if (this.handler.errorHandler && typeof this.handler.errorHandler === 'function') {
                            this.stopPlayback();

                            this.handler.errorHandler({
                                code: e?.code,
                                category: e?.category,
                                message: this.extractDrmErrorMessage(e?.data) || e?.message,
                            });
                        }

                        Sentry.captureException(e);
                    }
                }
            }
        }
    };

    preparePlayer = async (config: PlayerConfig) => {
        super.preparePlayer(config);

        window.shaka = shaka;

        const youboraTracking = YouboraTracking.getInstance();
        const { errorHandler, onInitialized, onNetworkError } = config;

        this.engine = new shaka.Player();
        await this.engine.attach(this.video);

        this.trackSegmentActivity();
        this.monitorPlaybackProgress();

        this.engine.addEventListener('buffering', (event) => {
            if (event?.buffering === false && this.isEngineLoaded()) {
                this.endStartupBuffering();
            }
        });

        this.video.addEventListener('playing', () => {
            this.streamingFailureRetries = 0;
            youboraTracking.setPlaybackStarted(true);
            youboraTracking.endRecoveryAttempt();
        });

        this.video.addEventListener('ended', () => {
            this.endOfStreamEmitted = true;
            youboraTracking.setPlaybackStarted(false);
        });
        this.engine.addEventListener('error', (event) => {
            const code = event?.detail?.code || event?.code;
            const category = event?.detail?.category || event?.category;
            const data = event?.detail?.data;
            const isNetworkError = category === 'network' || category === NETWORK_ERROR_CATEGORY || code === NETWORK_ERROR_CODE;

            if (RECOVERABLE_MEDIA_ERROR_CODES.includes(code) && this.asset?.stream_url) {
                this.log('warn', 'Recoverable media error, reloading', code);
                youboraTracking.startRecoveryAttempt();
                this.reloadStream(event?.detail ?? event);
                return;
            }

            if (isNetworkError) {
                youboraTracking.startRecoveryAttempt();

                if (!window.navigator.onLine && onNetworkError) {
                    onNetworkError();
                }
            } else if (errorHandler) {
                const message = this.extractDrmErrorMessage(data) || event?.detail?.message || null;

                if (STREAM_ERRORS.includes(message)) {
                    this.stopPlayback();
                }

                errorHandler({
                    code,
                    category,
                    message,
                });
            }
        });

        window.addEventListener('online', () => {
            youboraTracking.endRecoveryAttempt();

            this.recoverStream();

            if (youboraTracking.hasPlaybackStarted && this.video && this.video.paused && this.isEngineLoaded()) {
                this.play();
            }
        });

        window.addEventListener('offline', () => {
            youboraTracking.startRecoveryAttempt();
        });

        if (shaka.net.HttpXHRPlugin) {
            shaka.net.NetworkingEngine.registerScheme(
                'http',
                shaka.net.HttpXHRPlugin.parse,
                shaka.net.NetworkingEngine.PluginPriority.PREFERRED,
                true
            );
            shaka.net.NetworkingEngine.registerScheme(
                'https',
                shaka.net.HttpXHRPlugin.parse,
                shaka.net.NetworkingEngine.PluginPriority.PREFERRED,
                true
            );
        }

        if (onInitialized && typeof onInitialized === 'function') {
            onInitialized(this);
        }
    };

    resumeStreaming = (): boolean => {
        if (!this.engine) {
            return false;
        }

        this.streamingFailureRetries = 0;

        return this.engine.retryStreaming() === true;
    };

    recoverStream = (): void => {
        if (!this.engine || !this.asset?.stream_url || this.stoppedAtTime !== null || this.reloadInProgress) {
            return;
        }

        if (!this.reloadGaveUp && !this.reloadRetryAt && this.reloadResumeAt === null) {
            this.resumeStreaming();
            return;
        }

        this.streamReloads = 0;
        this.lastStreamReloadAt = 0;
        this.reloadRetryAt = 0;
        this.reloadStream();
    };

    play = () => {
        if (!this.video) {
            return;
        }

        this.video.play().catch((err) => {
            if (err?.name !== 'NotAllowedError' || this.video.muted) {
                console.error('video.play()', err);
                return;
            }

            this.log('warn', 'Autoplay blocked, retrying muted');
            this.video.muted = true;

            this.video.play().catch((retryError) => {
                this.video.muted = false;
                console.error('video.play()', retryError);
            });
        });
    };

    pause = () => {
        if (this.video) {
            this.video.pause();
        }
    };

    isPlaying = () => {
        if (this.video) {
            return !this.video.paused;
        }
        return false;
    };

    seekTo = (value) => {
        if (this.reloadResumeAt !== null) {
            this.reloadResumeAt = value;
        }

        if (this.video) {
            this.video.currentTime = value;
        }
    };

    getDuration = () => {
        if (this.video) {
            return this.video.duration || 0;
        }
        return 0;
    };

    getCurrentTime = () => {
        if (this.stoppedAtTime !== null) {
            return this.stoppedAtTime;
        }
        if (this.reloadResumeAt !== null) {
            return this.reloadResumeAt;
        }
        if (this.video) {
            return this.video.currentTime;
        }
        return 0;
    };

    getVolume = () => {
        if (this.video) {
            return this.video.volume;
        }
        return 0;
    };

    setVolume = (value: number) => {
        if (this.video && value >= 0) {
            this.video.volume = value;

            if (value > 0 && this.video.muted && navigator.userActivation?.hasBeenActive !== false) {
                this.video.muted = false;
            }
        }
    };

    mute = (status = false, userAction = false) => {
        if (status) {
            this.storedVolume = this.getVolume();
            this.setVolume(0);
        } else if (userAction) {
            this.setVolume(this.storedVolume);
        }
    };

    isMuted = () => {
        if (this.video) {
            return this.video.muted;
        }
        return false;
    };

    getBuffered = () => {
        if (this.video) {
            return this.video.buffered || [];
        }
        return [];
    };

    getAudioLanguages = (): any => this.getAudioTracks();

    getTextLanguages = (): any => this.getSubtitleTracks();

    getBitrateTracks = (): any => this.getVariantTracks();

    getThumbnailTrack = (): any => this.getImageTrack();

    getThumbnails = (id: number, pos: number): any => this.getThumbnailImage(id, pos);

    setAudioLanguage = (track: Track): void => {
        this.currentAudio = track;

        if (!this.engine) {
            console.warn('[Player] Cannot set audio language: engine not initialized');
            return;
        }

        const activeVariant = this.getActiveVariant();

        if (activeVariant && compareTracks(this.toAudioTrack(activeVariant), track)) {
            return;
        }

        const audioTrack = this.availableAudios.find((audio) => compareTracks(audio, track));

        if (audioTrack) {
            this.engine.selectVariantTrack(audioTrack.raw, true);
        }
    };

    setTextLanguage = (track: Track): void => {
        this.currentSubtitle = !track || track.language === SUBTITLE_NONE ? null : track;

        if (!track || track.language === SUBTITLE_NONE) {
            this.engine.selectTextTrack(null);
            return;
        }

        const subtitleTrack = this.availableSubtitles.find((subtitle) => compareTracks(subtitle, track));

        if (subtitleTrack) {
            this.engine.selectTextTrack(subtitleTrack.raw);
        }
    };

    setBitrateTrack = (track: BitrateTrack): void => {
        this.currentBitrate = track;

        if (!track || track.bandwidth === BITRATE_TRACK_AUTO) {
            return;
        }

        const bitrateTrack = this.availableBitrateTracks.find((video) => compareBitrateTracks(video, track));

        if (!bitrateTrack?.raw) {
            return;
        }

        const activeVariant = this.getActiveVariant();

        if (activeVariant && activeVariant.height === bitrateTrack.raw.height) {
            return;
        }

        this.engine.selectVariantTrack(bitrateTrack.raw, true);
    };

    getSelectedLanguage = () => this.currentAudio;

    getSelectedSubtitle = () => this.currentSubtitle;

    getSelectedBitrateTrack = () => this.currentBitrate;

    configureEngine = (config: any) => {
        const { startupRebufferingGoal, ...engineConfig } = config ?? {};

        if (typeof startupRebufferingGoal === 'number') {
            this.startupRebufferingGoal = startupRebufferingGoal;
        }

        const goal = engineConfig?.streaming?.rebufferingGoal;

        if (typeof goal !== 'number') {
            this.engine.configure(engineConfig);
            return;
        }

        this.steadyRebufferingGoal = goal;

        if (!this.startupBufferingActive) {
            this.engine.configure(engineConfig);
            return;
        }

        this.engine.configure({
            ...engineConfig,
            streaming: { ...engineConfig.streaming, rebufferingGoal: this.activeStartupGoal ?? goal },
        });
    };

    getYoubora = () => this.youbora;
}

export default ShakaPlayer;
